import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { displayPhDate, displayPhTime, formatPhDate } from '@/lib/phDate';
import type { FarmUserResponse } from '@/types/generated/openapi';
import { useAuth } from './useAuth';
import { useMembership } from './useMembership';

/**
 * "Ana · 7:05 pm" for supervisor+, null for everyone else and for rows with no author.
 * Imported rows carry no technician_id and their created_at is the import time, so they show nothing.
 */
export function useEnteredBy(farmId: string) {
  const { user } = useAuth();
  const { isSupervisorOrAbove } = useMembership();
  const { data: names, isPending } = useQuery({
    queryKey: qk.farmUsers.list(farmId),
    queryFn: () => api.get<FarmUserResponse[]>(`/api/v1/farms/${farmId}/users`),
    select: (rows) => new Map(rows.map((r) => [r.user_id, r.user.name || r.user.email.split('@')[0]])),
    enabled: isSupervisorOrAbove,
    staleTime: 5 * 60_000,
  });

  return (row: { technician_id?: string | null; created_at: string }, rowDate: string): string | null => {
    if (!isSupervisorOrAbove || isPending || !row.technician_id) return null;
    const who = row.technician_id === user?.id ? 'You' : names?.get(row.technician_id);
    const at = new Date(row.created_at);
    const when =
      formatPhDate(at) === rowDate
        ? displayPhTime(at)
        : `${displayPhDate(at, { month: 'short', day: 'numeric' })}, ${displayPhTime(at)}`;
    // Keep "· 3:40 AM" on one line when the cell wraps at phone width.
    const time = when.replace(/[ \u202f]/g, '\u00a0');
    return who ? `${who} ·\u00a0${time}` : time;
  };
}
